import { useTranslation } from 'react-i18next';
import Autocomplete, {
  AutocompleteRenderInputParams,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { TextV2 } from '@insights-ltd/design-library/components';
import { AutocompleteChangeReason, styled } from '@mui/material';
import { User } from 'types/types';
import { ListBoxComponent } from 'components/VirtualisedAutocomplete/ListBoxComponent';

type UserAutocompleteProps = {
  options: User[];
  onSelect: (value: User) => void;
  loading: boolean;
  disableClearable?: boolean;
  disabled?: boolean;
  selectedValue?: string;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  isNarrow?: boolean;
  clearToggle?: boolean;
};

const StyledP = styled(TextV2)({
  marginTop: '-7px !important',
  width: '298px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const UserAutocomplete = ({
  options,
  onSelect,
  loading,
  disableClearable = false,
  disabled,
  selectedValue,
  label,
  placeholder,
  error = false,
  helperText,
  required = false,
  isNarrow = false,
  clearToggle,
}: UserAutocompleteProps) => {
  const { t } = useTranslation();

  const getSelectedItem = () =>
    options?.length && selectedValue
      ? (options.find(({ id }) => id === selectedValue) as User) || ''
      : null;

  return (
    <Autocomplete
      id="user-search"
      key={clearToggle as any}
      data-testid="learners-autocomplete"
      disableClearable={disableClearable}
      autoSelect={disableClearable}
      options={options}
      value={getSelectedItem()}
      fullWidth
      disabled={disabled || undefined}
      loadingText={t(
        'ui.event-management.events.edit.practitioners.loading-text',
      )}
      noOptionsText={t(
        'ui.event-management.events.edit.practitioners.no-options-text',
      )}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_, value, reason) => {
        if (
          reason === ('selectOption' as AutocompleteChangeReason) &&
          value !== null
        ) {
          onSelect(value);
        }
      }}
      filterOptions={createFilterOptions({
        stringify: (option: User) =>
          `${option.fullName} ${option.emailAddress}`,
      })}
      getOptionLabel={(option) => option.fullName}
      renderOption={(fullProps, val) => {
        const { color, ...props } = fullProps;
        return (
          <li {...props} key={val.id} data-testid={`user-option-${val.id}`}>
            <TextV2 component="span" variant="bodyBold" color="primary">
              {val.fullName} {!isNarrow && ` - ${val.emailAddress}`}
            </TextV2>
            {isNarrow && <StyledP component="p">{val.emailAddress}</StyledP>}
          </li>
        );
      }}
      loading={loading}
      slots={{ listbox: ListBoxComponent }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          error={error}
          required={required}
          helperText={helperText}
          label={label}
          variant="outlined"
          placeholder={placeholder}
        />
      )}
    />
  );
};

export default UserAutocomplete;
